import React, { useRef, useMemo } from 'react'
import { FormikProps } from 'formik'
import { Alert, Checkbox, List, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

import { User, Request, RequestUserData } from 'src/types'
import UserAvatar from 'src/components/UserAvatar'

import * as CSS from './styles'

function userToRequestUserData(user: User): RequestUserData {
  return {
    cost_center: null,
    included: false,
    reason_id: null,
    reason: null,
    request_user_data_id: null,
    traveler: {
      gender: user.gender,
      name: `${user.first_name} ${user.last_name}`,
      user_id: user.user_id
    },
    user_id: user.user_id
  }
}

function userSort(a: RequestUserData, b: RequestUserData) {
  return a.traveler.name < b.traveler.name ? -1 : 1
}

interface Props {
  formikProps: FormikProps<Request>
  getUsers: (value: string) => void
  loading: boolean
  request: Request
  resetRequest(request_user_data: RequestUserData[]): void
  users: User[]
  verifyCanBeChange(): boolean
}

export default function SetupUserList({
  formikProps,
  getUsers,
  loading,
  resetRequest,
  users,
  verifyCanBeChange
}: Props) {
  const refTimeout = useRef(null)
  const { t } = useTranslation()

  const userList: RequestUserData[] = useMemo(() => {
    const request_user_data = formikProps.values.request_user_data || []
    return request_user_data
      .sort(userSort)
      .concat(
        users
          .filter(
            user =>
              !request_user_data
                .map(selected => selected.user_id)
                .includes(user.user_id)
          )
          .map(userToRequestUserData)
      )
  }, [users, formikProps.values.request_user_data])

  return (
    <>
      <CSS.Input
        data-test-id="traveler-search-name"
        onChange={({ target: { value } }) => {
          clearTimeout(refTimeout.current)
          refTimeout.current = setTimeout(() => {
            getUsers(value)
          }, 700)
        }}
        placeholder={t('web.setup.participants.search.placeholder')}
        size="large"
      />
      {typeof formikProps.errors.request_user_data === 'string' && (
        <Alert
          data-test-id="traveler-alert"
          message={t('web.setup.participants.add-some-participant')}
          type="warning"
        />
      )}
      <List
        data-test-id="traveler-list"
        dataSource={userList}
        itemLayout="horizontal"
        loading={loading}
        locale={{ emptyText: t('common.no-data') }}
        renderItem={item => (
          <List.Item
            data-test-id="traveler-item"
            key={item.user_id}
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()

              let found = false
              const request_user_data: RequestUserData[] =
                formikProps.values.request_user_data.reduce((acc, cur) => {
                  if (cur.user_id === item.user_id) {
                    found = true
                    return acc
                  }
                  return acc.concat(cur)
                }, [])

              if (!found) {
                request_user_data.push({
                  ...item,
                  included: true,
                  reason_id: formikProps.values.tmp_reason_id
                })
                request_user_data.sort(userSort)
              }

              if (verifyCanBeChange()) {
                formikProps.setFieldValue(
                  'request_user_data',
                  request_user_data
                )

                formikProps.setFieldTouched('request_user_data', true)
                return
              }

              Modal.confirm({
                cancelText: t('common.actions.cancel'),
                content: t(
                  'web.setup.participants.confirmation-changing-user-list'
                ),
                okText: t('common.actions.yes'),
                onOk: () => {
                  resetRequest(request_user_data)
                }
              })
            }}
          >
            <Checkbox checked={item.included}>
              <UserAvatar
                gender={item.traveler.gender}
                name={item.traveler.name}
                size="large"
              />
              <CSS.Username>{item.traveler.name}</CSS.Username>
            </Checkbox>
          </List.Item>
        )}
      />
    </>
  )
}
